import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Meeting,
  MeetingMember,
  WorkshopResource,
  meetingService,
  WorkshopCalendar,
} from '../../services/meetingService';
import { parseWorkshopCalendar } from '../../data/workshopCalendar';
import { WORKSHOP_2026_PATH } from '../../services/workshop2026Service';
import { formatFirstSessionDate } from '../../services/workshop2026Service';
import WorkshopParticipantsAdmin from './WorkshopParticipantsAdmin';
import WorkshopFirstStepSignupSettings from './WorkshopFirstStepSignupSettings';
import styles from '../../styles/meetingSettingsPage.module.css';

interface WorkshopFacilitatorSettingsProps {
  meeting: Meeting;
  meetingCode: string;
  members: MeetingMember[];
  canEdit: boolean;
  onReload: () => Promise<void>;
}

const STEP_OPTIONS = Array.from({ length: 12 }, (_, i) => i + 1);

function isoDateFromMeeting(meeting: Meeting): string {
  const raw = meeting.workshop_first_session_date?.trim() || '';
  return /^\d{4}-\d{2}-\d{2}$/.test(raw) ? raw : '';
}

function emptyResource(): WorkshopResource {
  return {
    id: `new-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    title: '',
    description: '',
    url: '',
  };
}

function resourcesFromMeeting(meeting: Meeting): WorkshopResource[] {
  const raw = meeting.workshop_resources;
  if (!Array.isArray(raw)) return [];
  return raw.map((r) => ({
    id: r.id || emptyResource().id,
    title: r.title || '',
    description: r.description || '',
    url: r.url || '',
  }));
}

const WorkshopFacilitatorSettings: React.FC<WorkshopFacilitatorSettingsProps> = ({
  meeting,
  meetingCode,
  members,
  canEdit,
  onReload,
}) => {
  const [name, setName] = useState(meeting.name ?? '');
  const [description, setDescription] = useState(meeting.description ?? '');
  const [zoomUrl, setZoomUrl] = useState(meeting.zoom_url ?? '');
  const [phoneListVisibilityMode, setPhoneListVisibilityMode] = useState<
    'off' | 'verified_only' | 'managers_only'
  >((meeting.phone_list_visibility_mode as 'off' | 'verified_only' | 'managers_only') ?? 'off');
  const [firstSessionDate, setFirstSessionDate] = useState(() => isoDateFromMeeting(meeting));
  const [calendar, setCalendar] = useState<WorkshopCalendar>(() => parseWorkshopCalendar(meeting));
  const [resources, setResources] = useState<WorkshopResource[]>(() => resourcesFromMeeting(meeting));
  const [savingBasics, setSavingBasics] = useState(false);
  const [savingCalendar, setSavingCalendar] = useState(false);
  const [savingResources, setSavingResources] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setName(meeting.name ?? '');
    setDescription(meeting.description ?? '');
    setZoomUrl(meeting.zoom_url ?? '');
    setPhoneListVisibilityMode(
      (meeting.phone_list_visibility_mode as 'off' | 'verified_only' | 'managers_only') ?? 'off'
    );
    setFirstSessionDate(isoDateFromMeeting(meeting));
    setCalendar(parseWorkshopCalendar(meeting));
    setResources(resourcesFromMeeting(meeting));
  }, [meeting]);

  const firstSession = formatFirstSessionDate(meeting);
  const workshopHome = `${WORKSHOP_2026_PATH}?tab=workshop`;

  const flash = (msg: string) => {
    setMessage(msg);
    setTimeout(() => setMessage(null), 3500);
  };

  const handleSaveBasics = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canEdit || savingBasics) return;
    setSavingBasics(true);
    setError(null);
    try {
      await meetingService.updateMeeting(meetingCode, {
        name: name.trim(),
        description: description.trim(),
        zoom_url: zoomUrl.trim(),
        phone_list_visibility_mode: phoneListVisibilityMode,
        workshop_first_session_date: firstSessionDate.trim(),
      });
      await onReload();
      flash('Workshop settings saved.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSavingBasics(false);
    }
  };

  const handleSaveCalendar = async () => {
    if (!canEdit || savingCalendar) return;
    setSavingCalendar(true);
    setError(null);
    try {
      await meetingService.updateMeeting(meetingCode, { workshop_calendar: calendar });
      await onReload();
      flash('Workshop calendar saved.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save calendar');
    } finally {
      setSavingCalendar(false);
    }
  };

  const handleSaveResources = async () => {
    if (!canEdit || savingResources) return;
    const incomplete = resources.some(
      (r) => (r.title.trim() && !r.url.trim()) || (!r.title.trim() && r.url.trim())
    );
    if (incomplete) {
      setError('Each resource needs both a title and a URL (or clear both to remove).');
      return;
    }
    setSavingResources(true);
    setError(null);
    try {
      const payload = resources
        .filter((r) => r.title.trim() && r.url.trim())
        .map((r) => ({
          id: r.id,
          title: r.title.trim(),
          description: (r.description || '').trim(),
          url: r.url.trim(),
        }));
      await meetingService.updateMeeting(meetingCode, { workshop_resources: payload });
      await onReload();
      flash('Workshop resources saved.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save resources');
    } finally {
      setSavingResources(false);
    }
  };

  const updateResource = (id: string, patch: Partial<WorkshopResource>) => {
    setResources((list) => list.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  };

  const removeResource = (id: string) => {
    setResources((list) => list.filter((r) => r.id !== id));
  };

  return (
    <>
      <p className={styles.workshopSettingsLead}>
        Manage the 2026 SAA Workshop for participants on{' '}
        <Link to={WORKSHOP_2026_PATH}>recoverycommons.com{WORKSHOP_2026_PATH}</Link>.
        {' '}
        <Link to={workshopHome}>Open workshop tab →</Link>
      </p>

      {message && <p className={styles.saveMessage}>{message}</p>}
      {error && <p className={styles.errorInline}>{error}</p>}

      <WorkshopParticipantsAdmin
        members={members}
        canEdit={canEdit}
        onMessage={flash}
        onError={setError}
      />

      <section className={styles.settingsSection}>
        <h2 className={styles.sectionTitle}>Workshop calendar</h2>
        <p className={styles.fieldHint}>
          Shown on the Workshop tab when published, or automatically after the first session date.
        </p>
        <div className={styles.form}>
          <label className={styles.label}>Current step</label>
          <div className={styles.calendarStepRow}>
            <select
              className={styles.select}
              value={calendar.current_step != null ? String(calendar.current_step) : ''}
              onChange={(e) =>
                setCalendar((c) => ({
                  ...c,
                  current_step: e.target.value === '' ? null : Number(e.target.value),
                }))
              }
              disabled={!canEdit}
            >
              <option value="">—</option>
              <option value="0">Start</option>
              {STEP_OPTIONS.map((n) => (
                <option key={n} value={n}>
                  Step {n}
                </option>
              ))}
            </select>
            <input
              type="text"
              className={styles.input}
              placeholder="Optional title (e.g. Resentments)"
              value={calendar.step_title || ''}
              onChange={(e) => setCalendar((c) => ({ ...c, step_title: e.target.value }))}
              disabled={!canEdit}
            />
          </div>
          <label className={styles.label}>We started this step</label>
          <input
            type="date"
            className={styles.input}
            value={
              calendar.step_started_date && /^\d{4}-\d{2}-\d{2}$/.test(calendar.step_started_date)
                ? calendar.step_started_date
                : ''
            }
            onChange={(e) => setCalendar((c) => ({ ...c, step_started_date: e.target.value }))}
            disabled={!canEdit}
          />
          <label className={styles.label}>Where we are</label>
          <textarea
            className={styles.textarea}
            rows={3}
            value={calendar.where_we_are || ''}
            onChange={(e) => setCalendar((c) => ({ ...c, where_we_are: e.target.value }))}
            disabled={!canEdit}
            placeholder="e.g. Week 2 of Step 4 inventories"
          />
          <label className={styles.label}>Likely complete</label>
          <input
            type="text"
            className={styles.input}
            value={calendar.likely_complete || ''}
            onChange={(e) => setCalendar((c) => ({ ...c, likely_complete: e.target.value }))}
            disabled={!canEdit}
            placeholder="Date or note (e.g. mid-October 2026)"
          />
          <label className={styles.checkboxRow}>
            <input
              type="checkbox"
              checked={!!calendar.published}
              onChange={(e) => setCalendar((c) => ({ ...c, published: e.target.checked }))}
              disabled={!canEdit}
            />
            Publish calendar to confirmed members (before first session)
          </label>
          <button
            type="button"
            className={styles.submitBtn}
            onClick={handleSaveCalendar}
            disabled={!canEdit || savingCalendar}
          >
            {savingCalendar ? 'Saving…' : 'Save calendar'}
          </button>
        </div>
      </section>

      <WorkshopFirstStepSignupSettings
        meetingCode={meetingCode}
        canEdit={canEdit}
        onReload={onReload}
      />

      <section className={styles.settingsSection}>
        <h2 className={styles.sectionTitle}>Workshop resources</h2>
        <p className={styles.fieldHint}>
          Links shown to confirmed members on the Workshop tab under &ldquo;Workshop Resources.&rdquo;
          Title is the clickable link; description appears under it.
        </p>
        <div className={styles.form}>
          {resources.length === 0 && (
            <p className={styles.fieldHint}>No resources yet. Add one below.</p>
          )}
          {resources.map((resource, index) => (
            <div key={resource.id} className={styles.resourceEditorCard}>
              <p className={styles.resourceEditorHeading}>Resource {index + 1}</p>
              <label className={styles.label}>Title</label>
              <input
                type="text"
                className={styles.input}
                value={resource.title}
                onChange={(e) => updateResource(resource.id, { title: e.target.value })}
                disabled={!canEdit}
                placeholder="e.g. Week 2 reading"
              />
              <label className={styles.label}>Description (optional)</label>
              <textarea
                className={styles.textarea}
                rows={2}
                value={resource.description || ''}
                onChange={(e) => updateResource(resource.id, { description: e.target.value })}
                disabled={!canEdit}
                placeholder="Short note shown under the link"
              />
              <label className={styles.label}>URL</label>
              <input
                type="text"
                className={styles.input}
                value={resource.url}
                onChange={(e) => updateResource(resource.id, { url: e.target.value })}
                disabled={!canEdit}
                placeholder="https://… or /path/on/site"
              />
              {canEdit && (
                <button
                  type="button"
                  className={styles.linkishBtn}
                  onClick={() => removeResource(resource.id)}
                >
                  Remove
                </button>
              )}
            </div>
          ))}
          {canEdit && (
            <div className={styles.participantExportRow}>
              <button
                type="button"
                className={styles.secondaryBtn}
                onClick={() => setResources((list) => [...list, emptyResource()])}
              >
                Add resource
              </button>
              <button
                type="button"
                className={styles.submitBtn}
                onClick={handleSaveResources}
                disabled={savingResources}
              >
                {savingResources ? 'Saving…' : 'Save resources'}
              </button>
            </div>
          )}
        </div>
      </section>

      <section className={styles.settingsSection}>
        <h2 className={styles.sectionTitle}>Workshop basics</h2>
        <form onSubmit={handleSaveBasics} className={styles.form}>
          <label className={styles.label}>Workshop name</label>
          <input
            type="text"
            className={styles.input}
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={!canEdit}
            required
          />
          <label className={styles.label}>Description (optional)</label>
          <textarea
            className={styles.textarea}
            rows={3}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            disabled={!canEdit}
          />
          <label className={styles.label}>First session date</label>
          <input
            type="date"
            className={styles.input}
            value={firstSessionDate}
            onChange={(e) => setFirstSessionDate(e.target.value)}
            disabled={!canEdit}
            required
          />
          <p className={styles.fieldHint}>
            {firstSessionDate
              ? `Shown to members as ${formatFirstSessionDate({ ...meeting, workshop_first_session_date: firstSessionDate })}.`
              : firstSession
                ? `Currently: ${firstSession}. Pick a date and save.`
                : 'Set the date of the first live session.'}
          </p>
          <label className={styles.label}>Zoom meeting URL</label>
          <input
            type="url"
            className={styles.input}
            value={zoomUrl}
            onChange={(e) => setZoomUrl(e.target.value)}
            placeholder="https://zoom.us/j/..."
            disabled={!canEdit}
          />
          <p className={styles.fieldHint}>
            Shown to confirmed members on the Workshop tab. Leave blank until ready.
          </p>
          <label className={styles.label}>Phone list visibility</label>
          <select
            className={styles.select}
            value={phoneListVisibilityMode}
            onChange={(e) =>
              setPhoneListVisibilityMode(e.target.value as 'off' | 'verified_only' | 'managers_only')
            }
            disabled={!canEdit}
          >
            <option value="off">Off</option>
            <option value="verified_only">Confirmed participants</option>
            <option value="managers_only">Managers & admins</option>
          </select>
          <p className={styles.fieldHint}>
            When enabled, confirmed members can view the opt-in phone list after the workshop starts.
          </p>
          <button type="submit" className={styles.submitBtn} disabled={!canEdit || savingBasics}>
            {savingBasics ? 'Saving…' : 'Save basics'}
          </button>
        </form>
      </section>
    </>
  );
};

export default WorkshopFacilitatorSettings;
